import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OnboardingWizard from '../components/OnboardingWizard';
import HolderNotificationPrefs, { HOLDER_NOTIF_DEFAULTS } from '../components/holder/HolderNotificationPrefs';
import theme from '../theme';
import { User, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADDR = /^0x[0-9a-fA-F]{40}$/;
const inputStyle = { width: '100%', padding: '12px 16px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, fontSize: 15, outline: 'none', boxSizing: 'border-box' };

const Header = ({ Icon, title, sub }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
    <div style={{ width: 48, height: 48, background: `${theme.colors.primary}15`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={24} color={theme.colors.primary} />
    </div>
    <div>
      <h3 style={{ fontFamily: theme.fonts.heading, fontSize: 20, fontWeight: 600, color: theme.colors.text.primary }}>{title}</h3>
      <p style={{ fontSize: 14, color: theme.colors.text.secondary }}>{sub}</p>
    </div>
  </div>
);

const BuyerOnboarding = ({ setUser }) => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({ name: '', walletAddress: '', prefs: HOLDER_NOTIF_DEFAULTS });
  const [error, setError] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const key = me ? `holderOnboarding:${me.id}` : null;

  useEffect(() => {
    axios.get(`${API}/auth/me`, auth).then(r => {
      setMe(r.data);
      const saved = JSON.parse(localStorage.getItem(`holderOnboarding:${r.data.id}`) || 'null');
      if (saved) { setForm(saved.form); setCurrentStep(saved.step || 0); }
      else setForm(f => ({ ...f, name: r.data.name || '' }));
    }).catch(() => setError('Could not load your account. Refresh the page to try again.'));
    axios.get(`${API}/buyer/settings`, auth).then(r => setDelivery(r.data.notification_delivery)).catch(() => {});
  }, []);

  useEffect(() => { if (key) localStorage.setItem(key, JSON.stringify({ form, step: currentStep })); }, [key, form, currentStep]);

  const handleComplete = async () => {
    setError(null);
    try {
      const res = await axios.post(`${API}/onboarding/buyer/complete`, {
        name: form.name.trim(), walletAddress: form.walletAddress.trim() || null, holder_notification_prefs: form.prefs,
      }, auth);
      if (key) localStorage.removeItem(key);
      if (res.data?.user && setUser) setUser(res.data.user);
      navigate('/buyer/dashboard');
    } catch (e) {
      setError(e?.response?.data?.detail || 'We could not finish setup. Your answers are saved — please try again.');
    }
  };

  const steps = [
    {
      label: 'Profile',
      content: (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Header Icon={User} title="Your Holder profile" sub="We've filled in what you gave us at sign-up." />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Display name</label>
          <input data-testid="holder-onb-name" style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}>Email</label>
          <input data-testid="holder-onb-email" style={{ ...inputStyle, color: theme.colors.text.muted }} value={me?.email || ''} disabled />
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 6 }}>Your sign-in email. Change it from Settings later if needed.</div>
        </div>
      ),
    },
    {
      label: 'Wallet (optional)',
      content: (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Header Icon={Wallet} title="Wallet — optional" sub="You can skip this and claim Kickbacks without one." />
          <div data-testid="holder-onb-wallet-help" style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.6, marginBottom: 14 }}>
            <p>A digital wallet (like MetaMask) holds NFTs. You only need one if a creator reserves a benefit for NFT owners.</p>
            <p>Typing an address here just saves it as <b>Unverified</b>. To prove it's yours, verify it later in Settings by signing a free message — no funds move.</p>
            <p>Never share your private key or recovery phrase. We will never ask for them.</p>
          </div>
          <input data-testid="holder-onb-wallet" style={{ ...inputStyle, fontFamily: 'monospace' }} value={form.walletAddress}
            onChange={e => setForm({ ...form, walletAddress: e.target.value })} placeholder="0x… (leave empty to skip)" />
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 8 }}>No payouts or bank details are needed. Payout settings appear only if a Kickback you hold offers payouts.</div>
        </div>
      ),
    },
    {
      label: 'Notifications',
      content: (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <HolderNotificationPrefs value={form.prefs} onChange={prefs => setForm({ ...form, prefs })} delivery={delivery} />
        </div>
      ),
    },
  ];

  return (
    <>
      {error && <div data-testid="holder-onb-error" style={{ maxWidth: 600, margin: '16px auto 0', padding: 12, borderRadius: 8, background: '#FEF2F2', color: '#991B1B', fontSize: 13 }}>{error}</div>}
      <OnboardingWizard
        steps={steps}
        currentStep={currentStep}
        onNext={() => {
          setError(null);
          if (currentStep === 0 && !form.name.trim()) { setError('Please add a display name.'); return; }
          if (currentStep === 1 && form.walletAddress.trim() && !ADDR.test(form.walletAddress.trim())) {
            setError("That doesn't look like a public wallet address (0x followed by 40 characters). Fix it or clear the field to skip."); return;
          }
          setCurrentStep(currentStep + 1);
        }}
        onBack={() => setCurrentStep(currentStep - 1)}
        onComplete={handleComplete}
        title="Holder setup"
        subtitle="Three quick steps. Only your name is required."
      />
    </>
  );
};

export default BuyerOnboarding;
